import React, { useCallback, useState, useMemo } from 'react';
import { usePostModalStore } from '../store/usePostModalStore';
import { Card, IconButton } from '../components/ui';
import { ImageOff } from 'lucide-react';
import GalleryPageSkeleton from '../components/Skeletons/GalleryPageSkeleton';
import { useGetPosts } from '../queries/post';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import GalleryFilterBar from '../components/Gallery/GalleryFilterBar';
import GalleryMasonryGrid from '../components/Gallery/GalleryMasonryGrid';
import GalleryMobileList from '../components/Gallery/GalleryMobileList';

const GalleryPage = () => {
  const { openModal } = usePostModalStore();
  const { openErrorModal } = useErrorModalStore();

  // ─── Filter state ──────────────────────────────────────────────────────────
  const [selectedTypes, setSelectedTypes]       = useState([]); // '1:1' | '9:16'
  const [selectedStatuses, setSelectedStatuses] = useState([]); // 'pending' | 'approved' | …

  const toggleType = (value) =>
    setSelectedTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const toggleStatus = (value) =>
    setSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const clearAll = () => {
    setSelectedTypes([]);
    setSelectedStatuses([]);
  };

  // ─── Data ──────────────────────────────────────────────────────────────────
  const handlePostsError = useCallback(
    (error) => {
      if (error?.status >= 500) {
        return openErrorModal(getErrorModalMessage('server_error'), 'server_error');
      }
      openErrorModal(getErrorModalMessage('onboarding_posts_error'), 'onboarding_posts_error');
    },
    [openErrorModal]
  );

  const { data: posts = [], isLoading } = useGetPosts(true, null, handlePostsError);

  // ─── Client-side filtering ─────────────────────────────────────────────────
  const filteredPosts = useMemo(() => {
    let result = posts;
    if (selectedTypes.length > 0) {
      result = result.filter((p) => selectedTypes.includes(p.aspectRatio));
    }
    if (selectedStatuses.length > 0) {
      result = result.filter((p) => selectedStatuses.includes(p.status));
    }
    return result;
  }, [posts, selectedTypes, selectedStatuses]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handlePostClick = (post) => {
    if (post.status === 'draft') return;
    openModal(post);
  };

  const downloadImage = async (imageUrl, filename) => {
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename || 'image.jpg';
      link.target = '_self';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(imageUrl, '_blank');
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  if (isLoading) return <GalleryPageSkeleton />;

  const hasFilters = selectedTypes.length > 0 || selectedStatuses.length > 0;
  const isEmpty = filteredPosts.length === 0;

  return (
    <div className='space-y-6 pb-10'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-bold' style={{ color: 'var(--text-primary)' }}>
          Content Gallery
        </h1>
        <div className='text-sm' style={{ color: 'var(--text-muted)' }}>
          {hasFilters ? `${filteredPosts.length} / ${posts.length}` : posts.length} posts
        </div>
      </div>

      {/* Filters */}
      <GalleryFilterBar
        selectedTypes={selectedTypes}
        selectedStatuses={selectedStatuses}
        onToggleType={toggleType}
        onToggleStatus={toggleStatus}
        onClearAll={clearAll}
        totalCount={posts.length}
        filteredCount={filteredPosts.length}
      />

      {/* Empty state */}
      {isEmpty ? (
        <Card>
          <Card.Body>
            <div className='text-center py-12'>
              <ImageOff className='w-16 h-16 mx-auto mb-4' style={{ color: 'var(--text-ghost)' }} />
              <h3 className='text-lg font-medium mb-2' style={{ color: 'var(--text-primary)' }}>
                {hasFilters ? 'No posts match your filters' : 'No posts available'}
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>
                {hasFilters
                  ? 'Try removing some filters to see more posts.'
                  : 'Posts will appear here once they are generated.'}
              </p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* Desktop: masonry grid */}
          <GalleryMasonryGrid
            posts={filteredPosts}
            onPostClick={handlePostClick}
            onDownload={downloadImage}
          />

          {/* Mobile: infinite scroll list */}
          <GalleryMobileList
            posts={filteredPosts}
            onPostClick={handlePostClick}
            onDownload={downloadImage}
          />
        </>
      )}
    </div>
  );
};

export default GalleryPage;
